import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Fir163jsyncService } from './fir163jsync.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir163jsync',
    templateUrl: './fir163jsync.component.html',
    styleUrls: ['./fir163jsync.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Fir163jsyncComponent implements OnInit, OnDestroy {

  private static readonly bridgeHostSelector = 'gtw-fir-163j-sync-ce';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private subscriptions = new Subscription();
  private baseGlobalParams: any = {};

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  rowData: any = {};

  // Modal configuration - following AngularJS pattern
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to trigger 163j sync for the selected entities?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Component properties following batchallocate pattern
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  showModal: boolean = false;
  syncSaveDtls: any[] = [];
  logged_in_user: string = '';
  modalName: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private fir163jsyncService: Fir163jsyncService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subscriptions.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = this.baseURLs;
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(this.baseGlobalParams);
    }
    this.filterParams = fp;
    // Grid batch actions (e.g. "Actions → 163j sync") dispatch the toolbar
    // selection as `activeScreen.data.gridData` — an array of
    // `{ row_id, data: <row> }` entries (gizmoGrid `base.batchSelect`).
    // `activeScreen.data.rowData` is only the FIRST selected row (single
    // object with column keys like LEID/ME_CODE/COMBINATION_KEY), so iterating
    // over it with `for (var key in rowData)` would walk the column names and
    // produce one fake "row" per column (LEID/ME_CODE/...) — which is exactly
    // what caused `checkLockedbj` to be filled with N empty rows whose
    // me_code defaulted to 'HOELIM' and combination_key to 0.
    //
    // Prefer gridData (batch array) when available; fall back to the single
    // rowData object (pencil / single-row action paths) wrapped so that
    // `processGridData` can iterate it uniformly.
    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(gridPayload) && gridPayload.length > 0) {
      const o: any = {};
      gridPayload.forEach((r: any, i: number) => {
        o[i] = r?.data !== undefined ? r : { data: r };
      });
      this.rowData = o;
    } else if (Array.isArray(rawRow)) {
      const o: any = {};
      rawRow.forEach((r: any, i: number) => {
        o[i] = r?.data !== undefined ? r : { data: r };
      });
      this.rowData = o;
    } else if (rawRow && typeof rawRow === 'object' && Object.keys(rawRow).length > 0) {
      // Two shapes can land here:
      //  (a) batch map  — `{ '0': { data: {...} }, '1': { data: {...} } }`
      //      produced by `activeScreen.data.selected.rowData` legacy plumbing
      //      (see existing spec).
      //  (b) single row — `{ LEID, ME_CODE, COMBINATION_KEY, ... }` written by
      //      `CeHostDialog.bridgeDataToActiveScreen` for batch toolbar
      //      actions when only the first row leaks through `rowData`.
      // Detect (a) by checking that the first value is a wrapped row with a
      // `data` object (and treat anything else as a single row to wrap).
      const firstKey = Object.keys(rawRow)[0];
      const firstVal = rawRow[firstKey];
      const looksLikeBatchMap =
        firstVal && typeof firstVal === 'object' && firstVal.data && typeof firstVal.data === 'object';
      this.rowData = looksLikeBatchMap ? rawRow : { 0: { data: rawRow } };
    } else {
      this.rowData = {};
    }
    if (this.gtwApp?.USER_SETTINGS?.user?.sso_id) {
      this.USER_SETTINGS = this.gtwApp.USER_SETTINGS;
      this.logged_in_user = String(this.gtwApp.USER_SETTINGS.user.sso_id);
    }
    if (this.gtwApp?.SERVER_CONFIG) {
      this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG;
    }
    this.globalService = this.baseGlobalParams || {};

    // Resolve modalName: CeHostDialog sets it on activeScreen.data.modal_name (flat),
    // not nested under modalScreen like legacy ModalFactory.response.data.modalScreen.modal_name
    const asData = (activeScreen as any).data || {};
    this.modalName = asData.modal_name
      || asData.modalName
      || this.baseGlobalParams?.modalScreen?.modal_name
      || '';
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 &&
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  private tryInit(): void {
    this.init();
  }

  initializeData(): void {
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }
    
    if (this.baseGlobalParams?.modalScreen?.modal_name) {
      this.modalName = this.baseGlobalParams.modalScreen.modal_name;
    }
    
    if (this.rowData) {
      this.processGridData();
    }
  }

  processGridData(): void {
    this.syncSaveDtls = [];
    if (this.rowData == null) {
      return;
    }

    const gridDataMultipleSelect: any = _.clone(this.rowData);
    if (gridDataMultipleSelect == undefined) {
      return;
    }

    // Guard against `rowData` being a bare single-row object (column-keyed),
    // which would otherwise be iterated as keys → produce 1 fake row per
    // column with all values undefined (root cause of the empty-checkLockedbj
    // bug that sent `me_code=HOELIM` / `combination_key=0` rows to the
    // backend). When the first value isn't a wrapped row (`{ data: {...} }`)
    // or a plain row object, fall back to treating the whole object as a
    // single row.
    const keys = Object.keys(gridDataMultipleSelect);
    const firstVal = keys.length ? gridDataMultipleSelect[keys[0]] : null;
    const looksLikeRowCollection =
      firstVal && typeof firstVal === 'object'
      && ((firstVal.data && typeof firstVal.data === 'object')
          || ('LEID' in firstVal) || ('COMBINATION_KEY' in firstVal)
          || ('ME_CODE' in firstVal) || ('CDR_NO' in firstVal));

    const rows: any[] = looksLikeRowCollection
      ? keys.map(k => gridDataMultipleSelect[k])
      : [{ data: gridDataMultipleSelect }];

    rows.forEach((entry: any) => {
      const dataItem = entry?.data ?? entry;
      if (!dataItem || typeof dataItem !== 'object') {
        return;
      }
      this.syncSaveDtls.push({
        object_id: dataItem.object_id,
        combination_key: dataItem.COMBINATION_KEY,
        leid: dataItem.LEID,
        cdr_number: dataItem.CDR_NO,
        me_code: dataItem.ME_CODE || 'HOELIM',
      });
    });
  }

  onConfirm(): void {
    this.confirmSync();
  }

  /**
   * Close the dialog. `detail` is forwarded to `CeHostDialog.listenForEvents`:
   *   - 'cancel'  → dialog closes with `{ action: 'close' }` (no grid refresh)
   *   - anything else (e.g. 'refresh') → dialog closes with
   *     `{ action: 'refresh', detail }` so `grid.directive` reloads the grid.
   */
  cancel(detail: string = 'cancel'): void {
    this.showModal = false;
    this.requestGridUpdate.emit(detail);
  }
  
  onCancel(): void {
    this.cancel('cancel');
  }

  confirmSync(): void {
    const returnClobSettingsObj: any = {};
    const ecSaveDtls: any[] = [];
    
    const ccKeys = this.syncSaveDtls.map(item => item.combination_key);
    const ccKeysString = ccKeys.join(',');
    
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['combination_key'] = ccKeysString;

    const message = "163j will be synced for selected entities";
    
    this.sendDetails(returnClobSettingsObj, ecSaveDtls, message);
  }

  sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string): void {
    // CRITICAL: do NOT call `this.cancel()` before firing the HTTP request.
    // The previous flow was:
    //   1. cancel()  → CeHostDialog closes the MatDialog → this component
    //      is destroyed → `ngOnDestroy` calls `this.subscriptions.unsubscribe()`
    //      so the Subscription container is now CLOSED.
    //   2. openSnackBar(message, 'green')  ← shown BEFORE the API even
    //      responds, so the user always saw "synced" even when the backend
    //      had returned an error (or, as in this bug, never responded).
    //   3. triggerSync(...).subscribe(...) fires the XHR.
    //   4. `this.subscriptions.add(sub)` on a CLOSED Subscription instantly
    //      unsubscribes `sub` (RxJS contract) → Angular HttpClient aborts the
    //      in-flight request → Network panel shows "cancelled" against
    //      `saveJsonObject?action_code=vr35gl`.
    // Fix: keep the modal open with a loading state, wait for the API to
    // respond, then close with the right `detail` so the grid actually
    // refreshes on success, and surface the real backend message on error.
    this.modalConfig = { ...this.modalConfig, loading: true };

    const apiBase = this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api || '';
    const sub = this.fir163jsyncService.triggerSync(
      apiBase,
      returnClobSettingsObj,
      ecSaveDtls,
      this.syncSaveDtls,
      this.modalName,
      this.globalService,
      this.filterParams,
      this.rowData
    ).subscribe(
      (result: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        const errMsg = result?.errorMessage;
        const hasError = errMsg && errMsg !== 'null';
        if (hasError) {
          this.gtwSnackbarService.openSnackBar(String(errMsg), 'gtw-snackbar--red');
          this.cancel('cancel');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.cancel('refresh');
        }
      },
      (error: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        console.error('[fir163jsync] triggerSync error:', error);
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while triggering 163j sync. Please try again.',
          'gtw-snackbar--red'
        );
        this.cancel('cancel');
      }
    );
    this.subscriptions.add(sub);
  }

}
